/**
 * Hariom ERP - Purchase store (persisted).
 * Holds the goods price-comparison catalog, per-department monthly tool
 * requirements, and generated purchase orders. Seeded once from the Excel.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Good, DeptReq } from '../data/purchaseSeed'
import { sharedStorage } from './sharedStorage'

export interface POItem {
  name: string
  size: string
  dept: string
  qty: number
  vendor: string
  price: number
  lastPrice: number
}

export interface PurchaseOrder {
  id: string
  poNo: string
  date: string
  items: POItem[]
  total: number
  remarks: string
  generatedBy: string   // name of the user who generated this PO
}

interface PurchaseState {
  goods: Good[]
  depts: DeptReq[]
  orders: PurchaseOrder[]
  seedVersion: number
  setGoods: (g: Good[]) => void
  setDepts: (d: DeptReq[]) => void
  addOrder: (o: PurchaseOrder) => void
  removeOrder: (id: string) => void
  setSeedVersion: (v: number) => void
}

export const usePurchaseStore = create<PurchaseState>()(
  persist(
    (set) => ({
      goods: [],
      depts: [],
      orders: [],
      seedVersion: 0,
      setGoods: (g) => set({ goods: g }),
      setDepts: (d) => set({ depts: d }),
      addOrder: (o) => set((s) => ({ orders: [o, ...s.orders] })),
      removeOrder: (id) => set((s) => ({ orders: s.orders.filter((o) => o.id !== id) })),
      setSeedVersion: (v) => set({ seedVersion: v }),
    }),
    {
      name: 'hariom-erp-purchase',
      storage: createJSONStorage(() => sharedStorage), // shared across devices
      partialize: (s) => ({ goods: s.goods, depts: s.depts, orders: s.orders, seedVersion: s.seedVersion }),
    }
  )
)
